import React, { useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Tooltip,
  FormGroup,
  Checkbox,
  InputAdornment,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, parseISO, differenceInBusinessDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import ServiceSheetSummary from './ServiceSheetSummary';
import PrintableServiceSheet from './PrintableServiceSheet';
import SchoolYearManager from './SchoolYearManager';
import MigrationReports from './MigrationReports';
import { Add as AddIcon, Event as EventIcon, Assignment as AssignmentIcon } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import { db, auth } from '../../firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs, addDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Holidays from 'date-holidays';

// Composant TabPanel pour gérer l'affichage des onglets
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings = () => {
  const { userData } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [establishmentInfo, setEstablishmentInfo] = useState({
    name: '',
    academie: '',
    zoneAcademique: '',
    heures: '',
    principal: '',
    gestionnaire: '',
    responsableEncadrement: '',
    anneeScolaire: '',
    moisDebut: 6, // Juillet par défaut (0-11, où 0 est janvier)
    moisFin: 7, // Août par défaut (0-11, où 0 est janvier)
    anneeDebut: new Date().getFullYear(), // Année de début par défaut (année courante)
    anneeFin: new Date().getFullYear(), // Année de fin par défaut (année courante)
    ajustementHoraire: false,
    logo: '',
    address: '',
    phone: '',
    email: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [vacances, setVacances] = useState([]);
  const [sousComptes, setSousComptes] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newVacances, setNewVacances] = useState({ nom: '', type: 'PRINTEMPS', debut: null, fin: null });
  const [editMode, setEditMode] = useState(false);
  const [selectedVacanceId, setSelectedVacanceId] = useState(null);
  const [editVacances, setEditVacances] = useState(null);
  const [vacancesDialogOpen, setVacancesDialogOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidaysYear, setHolidaysYear] = useState(new Date().getFullYear());
  const [showHolidaysInCalendar, setShowHolidaysInCalendar] = useState(false);
  
  // États pour les fiches de service annuel
  const [serviceSheets, setServiceSheets] = useState([]);
  const [serviceSheetDialogOpen, setServiceSheetDialogOpen] = useState(false);
  const [serviceSheetEditMode, setServiceSheetEditMode] = useState(false);
  const [selectedServiceSheetId, setSelectedServiceSheetId] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [currentServiceSheet, setCurrentServiceSheet] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedPrintServiceSheetId, setSelectedPrintServiceSheetId] = useState(null);
  const [newServiceSheet, setNewServiceSheet] = useState({
    annee: new Date().getFullYear(),
    service: "",
    heuresBase: 1607,
    semainesTravaillees: 36,
    heuresParSemaine: 40,
    joursEnseignement: 0,
    heuresFeries: 0,
    heuresFeriesParJour: 8, // Par défaut, 8h par jour
    heuresFractionnees: 0,
    heuresDues: 1535,
    permanences: [],
    vacancesPeriodes: []
  });

  const typeOptions = [
    'PRINTEMPS',
    'ETE',
    'AUTOMNE',
    'HIVER',
    'AUTRE'
  ];

  useEffect(() => {
    fetchEstablishmentInfo();
    fetchVacances();
    fetchSousComptes();
    loadHolidays();
    fetchServiceSheets();
  }, [userData, holidaysYear]);

  const fetchEstablishmentInfo = async () => {
    if (userData?.establishmentId) {
      try {
        const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
        if (establishmentDoc.exists()) {
          const data = establishmentDoc.data();
          
          // Récupérer les années de début et de fin
          const anneeDebut = data.anneeDebut !== undefined ? data.anneeDebut : new Date().getFullYear();
          const anneeFin = data.anneeFin !== undefined ? data.anneeFin : new Date().getFullYear();
          
          // Générer l'année scolaire si elle n'existe pas
          let anneeScolaire = data.anneeScolaire || '';
          if (!anneeScolaire && anneeDebut && anneeFin) {
            anneeScolaire = `${anneeDebut}-${anneeFin}`;
          }
          
          // Si un logo existe, définir l'aperçu
          if (data.logo) {
            setLogoPreview(data.logo);
          }
          
          setEstablishmentInfo({
            name: data.name || '',
            academie: data.academie || '',
            zoneAcademique: data.zoneAcademique || '',
            heures: data.heures || '',
            principal: data.principal || '',
            gestionnaire: data.gestionnaire || '',
            responsableEncadrement: data.responsableEncadrement || '',
            anneeScolaire: anneeScolaire,
            moisDebut: data.moisDebut !== undefined ? data.moisDebut : 6, // Juillet par défaut
            moisFin: data.moisFin !== undefined ? data.moisFin : 7, // Août par défaut
            anneeDebut: anneeDebut,
            anneeFin: anneeFin,
            ajustementHoraire: data.ajustementHoraire || false,
            logo: data.logo || '',
            address: data.address || '',
            phone: data.phone || '',
            email: data.email || ''
          });
          
          // Récupérer la préférence d'affichage des jours fériés
          setShowHolidaysInCalendar(data.showHolidaysInCalendar || false);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des informations:', error);
      }
    }
  };

  const fetchVacances = async () => {
    if (userData?.establishmentId) {
      try {
        const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
        const querySnapshot = await getDocs(vacancesRef);
        const vacancesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            debut: data.debut?.toDate ? data.debut.toDate() : null,
            fin: data.fin?.toDate ? data.fin.toDate() : null
          };
        });
        
        // Filtrer et trier les périodes de vacances par date de début (ordre croissant)
        const filteredAndSortedVacances = vacancesData
          .filter(v => v.debut && v.fin)
          .sort((a, b) => {
            // Trier par date de début dans l'ordre croissant
            if (a.debut && b.debut) {
              return new Date(a.debut) - new Date(b.debut);
            }
            return 0;
          });
        
        setVacances(filteredAndSortedVacances);
      } catch (error) {
        console.error('Erreur lors de la récupération des périodes de vacances:', error);
      }
    }
  };

  const fetchSousComptes = async () => {
    if (userData?.establishmentId) {
      try {
        const q = query(
          collection(db, 'users'),
          where('establishmentId', '==', userData.establishmentId),
          where('role', '==', 'chefService')
        );
        const querySnapshot = await getDocs(q);
        const sousComptesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSousComptes(sousComptesData);
      } catch (error) {
        console.error('Erreur lors de la récupération des sous-comptes:', error);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fonction pour charger les jours fériés
  const loadHolidays = async () => {
    if (!userData?.establishmentId) return;
    
    setHolidaysLoading(true);
    try {
      // Récupérer les informations de l'établissement pour connaître la période de l'année scolaire
      const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
      if (establishmentDoc.exists()) {
        const data = establishmentDoc.data();
        
        // Récupérer la préférence d'affichage des jours fériés
        setShowHolidaysInCalendar(data.showHolidaysInCalendar || false);
        
        // Générer les jours fériés pour l'année scolaire (juillet à août de l'année suivante)
        const startYear = holidaysYear;
        const endYear = holidaysYear + 1;
        
        
        // Utiliser la bibliothèque date-holidays pour générer les jours fériés français
        const hd = new Holidays('FR');
        
        // Récupérer les jours fériés pour les deux années
        const holidaysStartYear = hd.getHolidays(startYear);
        const holidaysEndYear = hd.getHolidays(endYear);
        
        // Combiner les jours fériés des deux années
        const allHolidays = [...holidaysStartYear, ...holidaysEndYear];
        
        // Filtrer les jours fériés pour ne garder que ceux de l'année scolaire (juillet à août)
        const filteredHolidays = allHolidays.filter(holiday => {
          const holidayDate = new Date(holiday.date);
          const month = holidayDate.getMonth();
          const year = holidayDate.getFullYear();
          
          // Garder les jours fériés de juillet à décembre de l'année de début
          // et de janvier à août de l'année de fin
          return (year === startYear && month >= 6) || (year === endYear && month <= 7);
        });
        
        // Formater les jours fériés pour l'affichage
        const formattedHolidays = filteredHolidays.map(holiday => ({
          date: format(new Date(holiday.date), 'yyyy-MM-dd'),
          name: holiday.name,
          type: holiday.type
        }));
        
        setHolidays(formattedHolidays);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des jours fériés:', error);
    } finally {
      setHolidaysLoading(false);
    }
  };
  
  // Fonction pour récupérer les fiches de service annuel
  const fetchServiceSheets = async () => {
    if (!userData?.establishmentId) return;
    
    try {
      const serviceSheetRef = collection(db, 'establishments', userData.establishmentId, 'serviceSheets');
      const querySnapshot = await getDocs(serviceSheetRef);
      const serviceSheetData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convertir les dates des périodes de vacances en objets Date
        const vacancesPeriodes = data.vacancesPeriodes?.map(periode => ({
          ...periode,
          debut: periode.debut?.toDate ? periode.debut.toDate() : periode.debut,
          fin: periode.fin?.toDate ? periode.fin.toDate() : periode.fin
        })) || [];
        
        return {
          id: doc.id,
          ...data,
          permanences: data.permanences || [],
          vacancesPeriodes: vacancesPeriodes
        };
      });
      setServiceSheets(serviceSheetData);
    } catch (error) {
      console.error('Erreur lors de la récupération des fiches de service:', error);
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Créer un aperçu du logo
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour enregistrer le logo directement dans Firestore en base64
  const handleLogoUpload = async () => {
    if (!logoFile || !userData?.establishmentId) return;
    
    try {
      setIsUploading(true);
      
      // Convertir l'image en base64
      const reader = new FileReader();
      
      // Créer une promesse pour attendre la fin de la lecture du fichier
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
      
      // Lire le fichier en tant que Data URL (base64)
      reader.readAsDataURL(logoFile);
      
      // Attendre que la lecture soit terminée
      const base64String = await base64Promise;
      
      // Vérifier la taille du fichier base64 (limite Firestore: 1MB)
      if (base64String.length > 900000) { // ~900KB pour être sûr
        alert('Le fichier est trop volumineux. Veuillez choisir une image plus petite (max 500KB recommandé).');
        setIsUploading(false);
        return;
      }
      
      // Mettre à jour le logo dans Firestore
      await updateDoc(doc(db, 'establishments', userData.establishmentId), {
        logo: base64String
      });
      
      // Mettre à jour l'état local
      setEstablishmentInfo({
        ...establishmentInfo,
        logo: base64String
      });
      
      alert('Logo téléchargé avec succès');
    } catch (error) {
      console.error('Erreur lors du téléchargement du logo:', error);
      alert('Erreur lors du téléchargement du logo: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateEstablishment = async () => {
    if (userData?.establishmentId) {
      try {
        // Vérifier que les mois sont valides
        if (establishmentInfo.moisDebut < 0 || establishmentInfo.moisDebut > 11 || 
            establishmentInfo.moisFin < 0 || establishmentInfo.moisFin > 11) {
          alert('Les mois sélectionnés ne sont pas valides');
          return;
        }

        // Vérifier que les années sont valides
        const currentYear = new Date().getFullYear();
        if (establishmentInfo.anneeDebut < currentYear - 5 || establishmentInfo.anneeDebut > currentYear + 5 || 
            establishmentInfo.anneeFin < currentYear - 5 || establishmentInfo.anneeFin > currentYear + 5) {
          alert('Les années sélectionnées ne sont pas valides');
          return;
        }

        // Vérifier que la période est cohérente
        if (establishmentInfo.anneeDebut > establishmentInfo.anneeFin) {
          alert('L\'année de début ne peut pas être postérieure à l\'année de fin');
          return;
        }

        // Si les années sont identiques, vérifier que les mois sont cohérents
        if (establishmentInfo.anneeDebut === establishmentInfo.anneeFin && 
            establishmentInfo.moisDebut > establishmentInfo.moisFin) {
          alert('Pour une même année, le mois de début ne peut pas être postérieur au mois de fin');
          return;
        }

        // Générer automatiquement l'année scolaire à partir des années de début et de fin
        const updatedEstablishmentInfo = {
          ...establishmentInfo,
          anneeScolaire: `${establishmentInfo.anneeDebut}-${establishmentInfo.anneeFin}`
        };

        await updateDoc(doc(db, 'establishments', userData.establishmentId), updatedEstablishmentInfo);
        setEstablishmentInfo(updatedEstablishmentInfo); // Mettre à jour l'état local
        alert('Informations mises à jour avec succès');
      } catch (error) {
        console.error('Erreur lors de la mise à jour des informations:', error);
        alert('Erreur lors de la mise à jour des informations');
      }
    }
  };
  
  // Fonction pour connecter à la base de données MySQL Hostinger
  const connectToMySQLDatabase = async () => {
    try {
      // Cette fonction est juste un placeholder pour montrer comment on pourrait
      // se connecter à une base de données MySQL externe
      // Dans une application React, cette connexion devrait être gérée côté serveur
      alert('La connexion à la base de données MySQL externe doit être gérée par une API backend sécurisée.');
      
      // Informations de connexion (à utiliser côté serveur uniquement)
      const mysqlConfig = {
        host: 'srv1762.hstgr.io', // ou '153.92.15.41'
        user: 'u975606199_edugestion',
        database: 'u975606199_edugestion'
        // Le mot de passe devrait être stocké de manière sécurisée
      };
      
    } catch (error) {
      console.error('Erreur lors de la connexion à MySQL:', error);
    }
  };

  const handleAddVacances = async () => {
    if (!userData?.establishmentId || !newVacances.debut || !newVacances.fin || !newVacances.nom || !newVacances.type) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    try {
      if (editMode && selectedVacanceId) {
        // Mode modification
        await updateDoc(doc(db, 'establishments', userData.establishmentId, 'vacances', selectedVacanceId), {
          nom: newVacances.nom,
          type: newVacances.type,
          debut: new Date(newVacances.debut),
          fin: new Date(newVacances.fin)
        });
        alert('Période de vacances modifiée avec succès');
      } else {
        // Mode ajout
        const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
        await addDoc(vacancesRef, {
          nom: newVacances.nom,
          type: newVacances.type,
          debut: new Date(newVacances.debut),
          fin: new Date(newVacances.fin)
        });
      }
      
      // Réinitialiser les états
      setVacancesDialogOpen(false);
      setNewVacances({ nom: '', type: 'PRINTEMPS', debut: null, fin: null });
      setEditMode(false);
      setSelectedVacanceId(null);
      fetchVacances();
    } catch (error) {
      console.error('Erreur lors de l\'ajout/modification de la période de vacances:', error);
      alert(`Erreur lors de l'${editMode ? 'modification' : 'ajout'} de la période de vacances`);
    }
  };

  const handleEditAccount = async () => {
    if (selectedAccount) {
      try {
        // Désactiver le bouton pendant la mise à jour
        const btnElement = document.querySelector('.edit-account-save-btn');
        if (btnElement) btnElement.disabled = true;
        
        // Mise à jour du document dans Firestore
        await updateDoc(doc(db, 'users', selectedAccount.id), {
          firstName: selectedAccount.firstName,
          lastName: selectedAccount.lastName,
          email: selectedAccount.email,
          department: selectedAccount.department,
          phone: selectedAccount.phone,
          canCreatePermanences: selectedAccount.canCreatePermanences || false
        });
        
        // Fermer la boîte de dialogue et rafraîchir les données
        setEditDialogOpen(false);
        await fetchSousComptes();
        
        // Afficher un message de confirmation
        alert('Le compte a été mis à jour avec succès');
      } catch (error) {
        console.error('Erreur lors de la mise à jour du compte:', error);
        alert(`Erreur lors de la mise à jour du compte: ${error.message}`);
      } finally {
        // Réactiver le bouton
        const btnElement = document.querySelector('.edit-account-save-btn');
        if (btnElement) btnElement.disabled = false;
      }
    }
  };

  const handleDeleteVacances = async (id) => {
    if (userData?.establishmentId) {
      try {
        await deleteDoc(doc(db, 'establishments', userData.establishmentId, 'vacances', id));
        fetchVacances();
      } catch (error) {
        console.error('Erreur lors de la suppression de la période de vacances:', error);
      }
    }
  };
  
  // Fonction pour ajouter ou mettre à jour une fiche de service annuel
  const handleAddServiceSheet = async () => {
    if (!userData?.establishmentId || !newServiceSheet.annee) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      // Préparer les périodes de vacances pour la sauvegarde
      const vacancesPeriodes = newServiceSheet.vacancesPeriodes?.map(periode => ({
        id: periode.id,
        nom: periode.nom,
        debut: periode.debut,
        fin: periode.fin,
        type: periode.type,
        // Conserver le nombre de permanences pour cette période
        permanences: periode.permanences || 0,
        // Calculer le nombre de jours ouvrés dans la période
        joursOuvres: differenceInBusinessDays(periode.fin, periode.debut) + 1
      })) || [];
      
      // Calculer le total des jours de vacances
      const totalJoursVacances = vacancesPeriodes.reduce((total, p) => total + p.joursOuvres, 0);
      
      // Calculer le total des jours de permanences pendant les vacances
      const totalJoursPermanencesVacances = vacancesPeriodes.reduce((total, p) => total + (p.permanences || 0), 0);
      
      // Calculer les heures de permanence pendant les vacances (8h par jour)
      const heuresPermanencesVacances = totalJoursPermanencesVacances * 8;
      
      // Calculer les heures de permanence hors vacances (8h par jour)
      const heuresPermanenceHorsVacances = newServiceSheet.permanences.reduce((total, p) => total + (p.jours * 8), 0);
      
      // Calculer les heures totales de permanence
      const heuresPermanence = heuresPermanencesVacances + heuresPermanenceHorsVacances;
      
      // Calculer les heures totales basées sur les semaines travaillées et les heures par semaine
      const heuresCalculees = newServiceSheet.semainesTravaillees * newServiceSheet.heuresParSemaine;
      
      // Préparer les données à sauvegarder
      const serviceSheetData = {
        ...newServiceSheet,
        heuresPermanence,
        totalJoursVacances,
        heuresCalculees,
        vacancesPeriodes,
        updatedAt: serverTimestamp()
      };
      
      // Si nous sommes en mode édition, mettre à jour le document existant
      if (serviceSheetEditMode && selectedServiceSheetId) {
        await updateDoc(
          doc(db, 'establishments', userData.establishmentId, 'serviceSheets', selectedServiceSheetId),
          serviceSheetData
        );
        alert('Fiche de service mise à jour avec succès');
      } else {
        // Sinon, créer un nouveau document
        serviceSheetData.createdAt = serverTimestamp();
        await addDoc(
          collection(db, 'establishments', userData.establishmentId, 'serviceSheets'),
          serviceSheetData
        );
        alert('Fiche de service ajoutée avec succès');
      }
      
      // Fermer le dialogue
      setServiceSheetDialogOpen(false);
      
      // Stocker la fiche de service actuelle pour l'affichage du récapitulatif
      const savedServiceSheet = {
        ...serviceSheetData,
        id: serviceSheetEditMode ? selectedServiceSheetId : 'new'
      };
      setCurrentServiceSheet(savedServiceSheet);
      
      // Ouvrir le dialogue de récapitulatif
      setSummaryDialogOpen(true);
      
      // Réinitialiser le formulaire
      setServiceSheetEditMode(false);
      setSelectedServiceSheetId(null);
      setNewServiceSheet({
        annee: new Date().getFullYear(),
        heuresBase: 1607,
        semainesTravaillees: 36,
        heuresParSemaine: 40,
        joursEnseignement: 36 * 5, // 5 jours par semaine
        heuresFeries: 0,
        heuresFeriesParJour: 8, // Par défaut, 8h par jour
        heuresFractionnees: 0,
        heuresDues: 1607, // Heures de base - (heures fériées + heures fractionnées)
        permanences: [],
        vacancesPeriodes: []
      });
      
      // Rafraîchir la liste des fiches de service
      fetchServiceSheets();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la fiche de service:', error);
      alert(`Erreur lors de la sauvegarde de la fiche de service: ${error.message}`);
    }
  };
  
  // Fonction pour éditer une fiche de service existante
  const handleEditServiceSheet = (sheet) => {
    // Préparer les périodes de vacances pour l'édition
    const vacancesPeriodes = sheet.vacancesPeriodes?.map(periode => ({
      ...periode,
      debut: periode.debut instanceof Date ? periode.debut : new Date(periode.debut),
      fin: periode.fin instanceof Date ? periode.fin : new Date(periode.fin)
    })) || [];
    
    // Charger les données dans le formulaire
    setNewServiceSheet({
      ...sheet,
      vacancesPeriodes: vacancesPeriodes
    });
    
    // Activer le mode édition
    setServiceSheetEditMode(true);
    setSelectedServiceSheetId(sheet.id);
    setServiceSheetDialogOpen(true);
  };
  
  // Fonction pour supprimer une fiche de service
  const handleDeleteServiceSheet = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fiche de service ?')) {
      if (userData?.establishmentId) {
        try {
          await deleteDoc(doc(db, 'establishments', userData.establishmentId, 'serviceSheets', id));
          fetchServiceSheets();
          alert('Fiche de service supprimée avec succès');
        } catch (error) {
          console.error('Erreur lors de la suppression de la fiche de service:', error);
          alert('Erreur lors de la suppression de la fiche de service');
        }
      }
    }
  };
  
  // Fonction pour ajouter une permanence à la fiche de service
  const handleAddPermanence = () => {
    const newPermanence = {
      id: Date.now().toString(),
      mois: '',
      jours: 0,
      dates: ''
    };
    
    setNewServiceSheet({
      ...newServiceSheet,
      permanences: [...newServiceSheet.permanences, newPermanence]
    });
  };
  
  // Fonction pour mettre à jour une permanence
  const handleUpdatePermanence = (id, field, value) => {
    const updatedPermanences = newServiceSheet.permanences.map(permanence => {
      if (permanence.id === id) {
        return { ...permanence, [field]: value };
      }
      return permanence;
    });
    
    setNewServiceSheet({
      ...newServiceSheet,
      permanences: updatedPermanences
    });
  };
  
  // Fonction pour supprimer une permanence
  const handleDeletePermanence = (id) => {
    const updatedPermanences = newServiceSheet.permanences.filter(permanence => permanence.id !== id);
    
    setNewServiceSheet({
      ...newServiceSheet,
      permanences: updatedPermanences
    });
  };

  const handleDeleteSousCompte = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce sous-compte ?')) {
      try {
        await deleteDoc(doc(db, 'users', id));
        setSousComptes(sousComptes.filter(compte => compte.id !== id));
        alert('Sous-compte supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression du sous-compte:', error);
        alert('Erreur lors de la suppression du sous-compte');
      }
    }
  };
  
  // Fonction pour supprimer le logo de l'établissement
  const handleDeleteLogo = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer le logo de l\'établissement ?')) {
      try {
        // Mettre à jour le document dans Firestore en supprimant le logo
        await updateDoc(doc(db, 'establishments', userData.establishmentId), {
          logo: ''
        });
        
        // Mettre à jour l'état local
        setEstablishmentInfo({
          ...establishmentInfo,
          logo: ''
        });
        
        // Réinitialiser les états du logo
        setLogoPreview(null);
        setLogoFile(null);
        
        alert('Logo supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression du logo:', error);
        alert('Erreur lors de la suppression du logo: ' + error.message);
      }
    }
  };

  const handleEditVacances = (periode) => {
    setNewVacances({
      nom: periode.nom,
      type: periode.type,
      debut: periode.debut, // Les dates sont déjà converties dans fetchVacances
      fin: periode.fin     // Les dates sont déjà converties dans fetchVacances
    });
    setSelectedVacanceId(periode.id);
    setEditMode(true);
    setVacancesDialogOpen(true);
  };

  // Composant pour le dialogue d'ajout/modification de périodes de vacances
  const VacancesDialog = () => (
    <Dialog 
      open={vacancesDialogOpen} 
      onClose={() => {
        setVacancesDialogOpen(false);
        setNewVacances({ nom: '', type: 'PRINTEMPS', debut: null, fin: null });
        setEditMode(false);
        setSelectedVacanceId(null);
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{editMode ? 'Modifier la période de vacances' : 'Ajouter une période de vacances'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                value={newVacances.nom}
                onChange={(e) => setNewVacances({ ...newVacances, nom: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newVacances.type}
                  label="Type"
                  onChange={(e) => setNewVacances({ ...newVacances, type: e.target.value })}
                >
                  {typeOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date de début"
                  value={newVacances.debut}
                  onChange={(newValue) => setNewVacances({ ...newVacances, debut: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <DatePicker
                  label="Date de fin"
                  value={newVacances.fin}
                  onChange={(newValue) => setNewVacances({ ...newVacances, fin: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setVacancesDialogOpen(false);
          setNewVacances({ nom: '', type: 'PRINTEMPS', debut: null, fin: null });
          setEditMode(false);
          setSelectedVacanceId(null);
        }}>Annuler</Button>
        <Button onClick={handleAddVacances} variant="contained" color="primary">
          {editMode ? 'Enregistrer les modifications' : 'Ajouter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  // Composant pour le dialogue d'ajout/modification de fiches de service annuel
  const ServiceSheetDialog = () => (
    <Dialog 
      open={serviceSheetDialogOpen} 
      onClose={() => {
        setServiceSheetDialogOpen(false);
        setServiceSheetEditMode(false);
        setSelectedServiceSheetId(null);
        setNewServiceSheet({
          annee: new Date().getFullYear(),
          service: "",
          heuresBase: 1607,
          semainesTravaillees: 36,
          heuresParSemaine: 40,
          joursEnseignement: 36 * 5, // 5 jours par semaine
          heuresFeries: 0,
          heuresFeriesParJour: 8, // Par défaut, 8h par jour
          heuresFractionnees: 0,
          heuresDues: 1607, // Heures de base - (heures fériées + heures fractionnées)
          permanences: [],
          vacancesPeriodes: []
        });
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{serviceSheetEditMode ? 'Modifier la fiche de service annuel' : 'Créer une fiche de service annuel'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
            OBLIGATIONS HORAIRES POUR L'ANNÉE {newServiceSheet.annee}-{newServiceSheet.annee + 1}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Année scolaire"
                type="number"
                value={newServiceSheet.annee}
                onChange={(e) => {
                  const annee = parseInt(e.target.value);
                  setNewServiceSheet({ ...newServiceSheet, annee });
                }}
                helperText={`Année scolaire ${newServiceSheet.annee}-${newServiceSheet.annee + 1}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Service"
                value={newServiceSheet.service}
                onChange={(e) => {
                  setNewServiceSheet({ ...newServiceSheet, service: e.target.value });
                }}
                helperText="Nom du service (ex: Administratif, Pédagogique, etc.)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heures de base"
                type="number"
                value={newServiceSheet.heuresBase}
                onChange={(e) => {
                  const heuresBase = parseInt(e.target.value) || 0;
                  // Recalculer les heures dues
                  const heuresDues = heuresBase - newServiceSheet.heuresFeries - newServiceSheet.heuresFractionnees;
                  setNewServiceSheet({ ...newServiceSheet, heuresBase, heuresDues });
                }}
                helperText="Nombre d'heures de base annuelles"
                InputProps={{
                  endAdornment: <InputAdornment position="end">h</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de semaines travaillées"
                type="number"
                value={newServiceSheet.semainesTravaillees}
                onChange={(e) => {
                  const semainesTravaillees = parseInt(e.target.value);
                  // Recalculer les jours d'enseignement (5 jours par semaine)
                  const joursEnseignement = semainesTravaillees * 5;
                  setNewServiceSheet({ ...newServiceSheet, semainesTravaillees, joursEnseignement });
                }}
                helperText="Ex: 36 semaines"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heures par semaine"
                type="number"
                value={newServiceSheet.heuresParSemaine}
                onChange={(e) => {
                  const heuresParSemaine = parseInt(e.target.value);
                  setNewServiceSheet({ ...newServiceSheet, heuresParSemaine });
                }}
                helperText="Ex: 40h par semaine"
                InputProps={{
                  endAdornment: <InputAdornment position="end">h</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Jours d'enseignement"
                type="number"
                value={newServiceSheet.joursEnseignement}
                onChange={(e) => {
                  const joursEnseignement = parseInt(e.target.value);
                  setNewServiceSheet({ ...newServiceSheet, joursEnseignement });
                }}
                helperText="Calculé automatiquement (5 jours x semaines travaillées)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Jours fériés"
                  type="number"
                  value={(() => {
                    const joursFeries = newServiceSheet.heuresFeries / newServiceSheet.heuresFeriesParJour;
                    // Arrondir au nombre supérieur si la partie décimale est supérieure à 0.5
                    return Math.floor(joursFeries) + (joursFeries % 1 > 0.5 ? 1 : Math.ceil(joursFeries % 1));
                  })()}
                  onChange={(e) => {
                    const joursFeries = parseFloat(e.target.value) || 0;
                    const heuresFeries = joursFeries * newServiceSheet.heuresFeriesParJour;
                    // Recalculer les heures dues
                    const heuresDues = newServiceSheet.heuresBase - heuresFeries - newServiceSheet.heuresFractionnees;
                    setNewServiceSheet({ ...newServiceSheet, heuresFeries, heuresDues });
                  }}
                  helperText="Nombre de jours fériés (arrondi si décimale > 0.5)"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">jours</InputAdornment>,
                  }}
                />
              </Box>
              <FormControl component="fieldset">
                <FormLabel component="legend">Calcul des jours fériés</FormLabel>
                <RadioGroup
                  row
                  value={newServiceSheet.heuresFeriesParJour}
                  onChange={(e) => {
                    const heuresFeriesParJour = parseInt(e.target.value);
                    // Recalculer les heures fériées en fonction du nouveau nombre d'heures par jour
                    const joursFeries = newServiceSheet.heuresFeries / newServiceSheet.heuresFeriesParJour;
                    const heuresFeries = joursFeries * heuresFeriesParJour;
                    // Recalculer les heures dues
                    const heuresDues = newServiceSheet.heuresBase - heuresFeries - newServiceSheet.heuresFractionnees;
                    setNewServiceSheet({ ...newServiceSheet, heuresFeriesParJour, heuresFeries, heuresDues });
                  }}
                >
                  <FormControlLabel value={7} control={<Radio />} label="7h par jour" />
                  <FormControlLabel value={8} control={<Radio />} label="8h par jour" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heures fractionnées"
                type="number"
                value={newServiceSheet.heuresFractionnees}
                onChange={(e) => {
                  const heuresFractionnees = parseInt(e.target.value) || 0;
                  // Recalculer les heures dues
                  const heuresDues = newServiceSheet.heuresBase - newServiceSheet.heuresFeries - heuresFractionnees;
                  setNewServiceSheet({ ...newServiceSheet, heuresFractionnees, heuresDues });
                }}
                helperText="Nombre d'heures fractionnées (déduites des heures de base)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">h</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heures dues"
                type="number"
                value={newServiceSheet.heuresDues}
                InputProps={{
                  readOnly: true,
                  endAdornment: <InputAdornment position="end">h</InputAdornment>,
                }}
                helperText="Heures dues après déduction des jours fériés (calculé automatiquement)"
              />
            </Grid>
          </Grid>
          
          {/* Intégration des périodes de vacances */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Périodes de vacances
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                disabled={!newServiceSheet.vacancesPeriodes || newServiceSheet.vacancesPeriodes.length === 0}
                onClick={() => {
                  // Calculer le nombre total de jours de permanences à dispatcher
                  // Formule: (heuresDues - (semainesTravaillees * heuresParSemaine)) / 8
                  const heuresPresenceEleves = newServiceSheet.semainesTravaillees * newServiceSheet.heuresParSemaine;
                  const heuresPermanencesTotales = newServiceSheet.heuresDues - heuresPresenceEleves;
                  // Arrondir au nombre inférieur pour les permanences
                  const joursPermanencesTotaux = Math.floor(heuresPermanencesTotales / 8);
                  
                  // Calculer les permanences déjà assignées hors vacances
                  const permanencesHorsVacances = newServiceSheet.permanences.reduce((total, p) => total + p.jours, 0);
                  
                  // Permanences à dispatcher dans les vacances
                  const permanencesADispatcher = Math.max(0, joursPermanencesTotaux - permanencesHorsVacances);
                  
                  if (permanencesADispatcher <= 0) {
                    alert('Aucune permanence à dispatcher. Vérifiez vos heures dues et vos semaines travaillées.');
                    return;
                  }
                  
                  // Calculer le nombre total de jours ouvrés dans toutes les périodes de vacances sélectionnées
                  const totalJoursOuvres = newServiceSheet.vacancesPeriodes.reduce((total, periode) => {
                    const joursOuvres = differenceInBusinessDays(periode.fin, periode.debut) + 1;
                    return total + joursOuvres;
                  }, 0);
                  
                  // Dispatcher proportionnellement aux jours ouvrés de chaque période
                  const updatedVacancesPeriodes = newServiceSheet.vacancesPeriodes.map(periode => {
                    const joursOuvres = differenceInBusinessDays(periode.fin, periode.debut) + 1;
                    const ratio = joursOuvres / totalJoursOuvres;
                    const permanences = Math.round(permanencesADispatcher * ratio);
                    return { ...periode, permanences };
                  });
                  
                  // Ajuster pour s'assurer que le total correspond exactement
                  let totalAssigne = updatedVacancesPeriodes.reduce((total, p) => total + p.permanences, 0);
                  let diff = permanencesADispatcher - totalAssigne;
                  
                  // Si différence, ajuster la période la plus longue
                  if (diff !== 0) {
                    const periodeIndex = updatedVacancesPeriodes
                      .map((p, i) => ({ index: i, joursOuvres: differenceInBusinessDays(p.fin, p.debut) + 1 }))
                      .sort((a, b) => b.joursOuvres - a.joursOuvres)[0].index;
                    
                    updatedVacancesPeriodes[periodeIndex].permanences += diff;
                  }
                  
                  // Mettre à jour l'état
                  setNewServiceSheet({
                    ...newServiceSheet,
                    vacancesPeriodes: updatedVacancesPeriodes
                  });
                  
                  alert(`${permanencesADispatcher} jours de permanences ont été répartis entre les périodes de vacances sélectionnées.`);
                }}
              >
                Calculer les permanences
              </Button>
            </Box>
            <FormGroup>
              {vacances.map((periode) => {
                // Vérifier si cette période est déjà sélectionnée
                const isSelected = newServiceSheet.vacancesPeriodes?.some(p => p.id === periode.id);
                
                return (
                  <Box key={periode.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Ajouter la période aux périodes sélectionnées avec 0 permanence par défaut
                              setNewServiceSheet({
                                ...newServiceSheet,
                                vacancesPeriodes: [...(newServiceSheet.vacancesPeriodes || []), {
                                  id: periode.id,
                                  nom: periode.nom,
                                  debut: periode.debut,
                                  fin: periode.fin,
                                  type: periode.type,
                                  permanences: 0 // Nombre de permanences par défaut
                                }]
                              });
                            } else {
                              // Retirer la période des périodes sélectionnées
                              setNewServiceSheet({
                                ...newServiceSheet,
                                vacancesPeriodes: newServiceSheet.vacancesPeriodes.filter(p => p.id !== periode.id)
                              });
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ flexGrow: 1, mr: 2 }}>
                          <Typography variant="body1">{periode.nom}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {format(periode.debut, 'dd/MM/yyyy')} - {format(periode.fin, 'dd/MM/yyyy')}
                          </Typography>
                        </Box>
                      }
                      sx={{ flexGrow: 1, mr: 2 }}
                    />
                    {isSelected && (
                      <TextField
                        label="Permanences"
                        type="number"
                        size="small"
                        sx={{ width: '120px' }}
                        value={newServiceSheet.vacancesPeriodes.find(p => p.id === periode.id)?.permanences || 0}
                        onChange={(e) => {
                          const permanences = parseInt(e.target.value) || 0;
                          setNewServiceSheet({
                            ...newServiceSheet,
                            vacancesPeriodes: newServiceSheet.vacancesPeriodes.map(p => 
                              p.id === periode.id ? { ...p, permanences } : p
                            )
                          });
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">jours</InputAdornment>,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {vacances.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  Aucune période de vacances disponible. Veuillez en créer dans l'onglet "Périodes de vacances".
                </Typography>
              )}
            </FormGroup>
          </Box>
          
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Périodes de permanence
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPermanence}
              sx={{ mb: 2 }}
            >
              Ajouter une période de permanence
            </Button>
            
            {newServiceSheet.permanences.length > 0 && (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Mois</TableCell>
                      <TableCell>Nombre de jours</TableCell>
                      <TableCell>Dates</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {newServiceSheet.permanences.map((permanence) => (
                      <TableRow key={permanence.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={permanence.mois}
                            onChange={(e) => handleUpdatePermanence(permanence.id, 'mois', e.target.value)}
                            placeholder="Ex: Octobre"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={permanence.jours}
                            onChange={(e) => handleUpdatePermanence(permanence.id, 'jours', parseInt(e.target.value))}
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={permanence.dates}
                            onChange={(e) => handleUpdatePermanence(permanence.id, 'dates', e.target.value)}
                            placeholder="Ex: 12,13,14,15"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePermanence(permanence.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setServiceSheetDialogOpen(false);
          setServiceSheetEditMode(false);
          setSelectedServiceSheetId(null);
          setNewServiceSheet({
            annee: new Date().getFullYear(),
            heuresBase: 1607,
            semainesTravaillees: 36,
            heuresParSemaine: 40,
            joursEnseignement: 36 * 5, // 5 jours par semaine
            heuresFeries: 0,
            heuresFeriesParJour: 8, // Par défaut, 8h par jour
            heuresFractionnees: 0,
            heuresDues: 1607, // Heures de base - (jours fériés + heures fractionnées)
            permanences: [],
            vacancesPeriodes: []
          });
        }}>Annuler</Button>
        <Button onClick={handleAddServiceSheet} variant="contained" color="primary">
          {serviceSheetEditMode ? 'Mettre à jour' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="lg">
      <Paper sx={{ mt: 3, p: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Informations générales" />
          <Tab label="Périodes de vacances" />
          <Tab label="Sous-comptes" />
          <Tab label="Jours Fériés" icon={<EventIcon />} iconPosition="start" />
          <Tab label="Fiches de service annuel" icon={<AssignmentIcon />} iconPosition="start" />
          <Tab label="Années Scolaires" />
          <Tab label="Rapports de Migration" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de l'établissement"
                value={establishmentInfo.name}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Académie"
                value={establishmentInfo.academie}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, academie: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zone académique"
                value={establishmentInfo.zoneAcademique}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, zoneAcademique: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heures"
                value={establishmentInfo.heures}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, heures: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Proviseur"
                value={establishmentInfo.principal}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, principal: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gestionnaire"
                value={establishmentInfo.gestionnaire}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, gestionnaire: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Responsable d'encadrement"
                value={establishmentInfo.responsableEncadrement}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, responsableEncadrement: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Adresse de l'établissement"
                value={establishmentInfo.address}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={establishmentInfo.phone}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={establishmentInfo.email}
                onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Logo de l'établissement
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {logoPreview && (
                  <Box 
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      border: '1px solid #ddd', 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <img 
                      src={logoPreview} 
                      alt="Logo de l'établissement" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  </Box>
                )}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoChange}
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Choisir un logo
                    </Button>
                  </label>
                  {logoFile && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleLogoUpload}
                      disabled={isUploading}
                      sx={{ ml: 2 }}
                    >
                      {isUploading ? 'Téléchargement...' : 'Télécharger'}
                    </Button>
                  )}
                  {logoPreview && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteLogo}
                      startIcon={<DeleteIcon />}
                      sx={{ ml: 2 }}
                    >
                      Supprimer le logo
                    </Button>
                  )}
                </Box>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Format recommandé: PNG ou JPG, taille maximale: 2MB
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Année scolaire"
                value={establishmentInfo.anneeScolaire || `${establishmentInfo.anneeDebut}-${establishmentInfo.anneeFin}`}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Généré automatiquement à partir des années de début et de fin"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Début d'année scolaire
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <FormControl fullWidth>
                    <InputLabel id="mois-debut-label">Mois</InputLabel>
                    <Select
                      labelId="mois-debut-label"
                      value={establishmentInfo.moisDebut}
                      label="Mois"
                      onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, moisDebut: e.target.value })}
                    >
                      <MenuItem value={0}>Janvier</MenuItem>
                      <MenuItem value={1}>Février</MenuItem>
                      <MenuItem value={2}>Mars</MenuItem>
                      <MenuItem value={3}>Avril</MenuItem>
                      <MenuItem value={4}>Mai</MenuItem>
                      <MenuItem value={5}>Juin</MenuItem>
                      <MenuItem value={6}>Juillet</MenuItem>
                      <MenuItem value={7}>Août</MenuItem>
                      <MenuItem value={8}>Septembre</MenuItem>
                      <MenuItem value={9}>Octobre</MenuItem>
                      <MenuItem value={10}>Novembre</MenuItem>
                      <MenuItem value={11}>Décembre</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={5}>
                  <FormControl fullWidth>
                    <InputLabel id="annee-debut-label">Année</InputLabel>
                    <Select
                      labelId="annee-debut-label"
                      value={establishmentInfo.anneeDebut}
                      label="Année"
                      onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, anneeDebut: e.target.value })}
                    >
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Fin d'année scolaire
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <FormControl fullWidth>
                    <InputLabel id="mois-fin-label">Mois</InputLabel>
                    <Select
                      labelId="mois-fin-label"
                      value={establishmentInfo.moisFin}
                      label="Mois"
                      onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, moisFin: e.target.value })}
                    >
                      <MenuItem value={0}>Janvier</MenuItem>
                      <MenuItem value={1}>Février</MenuItem>
                      <MenuItem value={2}>Mars</MenuItem>
                      <MenuItem value={3}>Avril</MenuItem>
                      <MenuItem value={4}>Mai</MenuItem>
                      <MenuItem value={5}>Juin</MenuItem>
                      <MenuItem value={6}>Juillet</MenuItem>
                      <MenuItem value={7}>Août</MenuItem>
                      <MenuItem value={8}>Septembre</MenuItem>
                      <MenuItem value={9}>Octobre</MenuItem>
                      <MenuItem value={10}>Novembre</MenuItem>
                      <MenuItem value={11}>Décembre</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={5}>
                  <FormControl fullWidth>
                    <InputLabel id="annee-fin-label">Année</InputLabel>
                    <Select
                      labelId="annee-fin-label"
                      value={establishmentInfo.anneeFin}
                      label="Année"
                      onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, anneeFin: e.target.value })}
                    >
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={establishmentInfo.ajustementHoraire}
                    onChange={(e) => setEstablishmentInfo({ ...establishmentInfo, ajustementHoraire: e.target.checked })}
                  />
                }
                label="Ajustement horaire"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleUpdateEstablishment}>
                Enregistrer les modifications
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Périodes de vacances</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setVacancesDialogOpen(true)}
            >
              Ajouter une période
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date de début</TableCell>
                  <TableCell>Date de fin</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vacances.map((periode) => (
                  <TableRow key={periode.id}>
                    <TableCell>{periode.nom}</TableCell>
                    <TableCell>{periode.type}</TableCell>
                    <TableCell>{periode.debut?.toLocaleDateString()}</TableCell>
                    <TableCell>{periode.fin?.toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEditVacances(periode)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteVacances(periode.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {VacancesDialog()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <List>
            {sousComptes.map((compte) => (
              <ListItem
                key={compte.id}
                secondaryAction={
                  <IconButton edge="end" onClick={() => {
                    setSelectedAccount(compte);
                    setEditDialogOpen(true);
                  }}>
                    <EditIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={`${compte.firstName} ${compte.lastName}`}
                  secondary={
                    <>
                      {`${compte.email} - ${compte.department}`}
                      <br />
                      <span style={{ color: compte.canCreatePermanences ? 'green' : 'red' }}>
                        {compte.canCreatePermanences ? 'Autorisé à créer des permanences' : 'Non autorisé à créer des permanences'}
                      </span>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Jours Fériés de l'année scolaire {holidaysYear}-{holidaysYear + 1} (juillet à août)
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={showHolidaysInCalendar}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setShowHolidaysInCalendar(newValue);
                    if (userData?.establishmentId) {
                      try {
                        await updateDoc(doc(db, 'establishments', userData.establishmentId), {
                          showHolidaysInCalendar: newValue
                        });
                      } catch (error) {
                        console.error('Erreur lors de la mise à jour de la préférence d\'affichage des jours fériés:', error);
                      }
                    }
                  }}
                />
              }
              label="Afficher les jours fériés dans le calendrier"
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
              <TextField
                label="Année"
                type="number"
                value={holidaysYear}
                onChange={(e) => setHolidaysYear(parseInt(e.target.value))}
                sx={{ width: 100, mr: 2 }}
              />
              <Button
                variant="contained"
                onClick={loadHolidays}
                disabled={holidaysLoading}
              >
                {holidaysLoading ? <CircularProgress size={24} /> : 'Charger les jours fériés'}
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell>Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holidays.map((holiday, index) => (
                    <TableRow key={index}>
                      <TableCell>{format(new Date(holiday.date), 'dd/MM/yyyy')}</TableCell>
                      <TableCell>{holiday.name}</TableCell>
                      <TableCell>{holiday.type}</TableCell>
                    </TableRow>
                  ))}
                  {holidays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {holidaysLoading ? 'Chargement...' : 'Aucun jour férié trouvé'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Fiches de service annuel
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setServiceSheetDialogOpen(true)}
              sx={{ mb: 2 }}
            >
              Ajouter une fiche de service
            </Button>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Année scolaire</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Heures de base</TableCell>
                    <TableCell>Semaines travaillées</TableCell>
                    <TableCell>Heures / semaine</TableCell>
                    <TableCell>Jours d'enseignement</TableCell>
                    <TableCell>Jours fériés</TableCell>
                    <TableCell>Heures dues</TableCell>
                    <TableCell>Nbre de jours de permanences</TableCell>
                    <TableCell>Vacances</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviceSheets.map((sheet) => (
                    <TableRow key={sheet.id}>
                      <TableCell>{sheet.annee}-{sheet.annee + 1}</TableCell>
                      <TableCell>{sheet.service || ""}</TableCell>
                      <TableCell>{sheet.heuresBase}</TableCell>
                      <TableCell>{sheet.semainesTravaillees || 36}</TableCell>
                      <TableCell>{sheet.heuresParSemaine || 40}</TableCell>
                      <TableCell>{sheet.joursEnseignement}</TableCell>
                      <TableCell>
                        {(() => {
                          // Utiliser heuresFeriesParJour s'il existe, sinon utiliser 8 par défaut
                          const heuresParJour = sheet.heuresFeriesParJour || 8;
                          const joursFeries = sheet.heuresFeries / heuresParJour;
                          // Arrondir au nombre supérieur si la partie décimale est supérieure à 0.5
                          const joursArrondis = Math.floor(joursFeries) + (joursFeries % 1 > 0.5 ? 1 : Math.ceil(joursFeries % 1));
                          return `${joursArrondis} jours (${heuresParJour}h/j)`;
                        })()}
                      </TableCell>
                      <TableCell>{sheet.heuresDues}</TableCell>
                      <TableCell>
                        {(() => {
                          // Calcul des heures travaillées en présence d'élèves
                          const heuresPresenceEleves = sheet.semainesTravaillees * sheet.heuresParSemaine || 0;
                          // Calcul des heures travaillées hors présence d'élèves (permanences)
                          const heuresPermanences = sheet.heuresDues - heuresPresenceEleves;
                          // Arrondir au nombre inférieur pour les permanences
                          const joursPermanences = Math.floor(heuresPermanences / 8);
                          
                          return sheet.permanences?.length > 0 ? (
                            <Tooltip title={sheet.permanences.map(p => `${p.mois}: ${p.jours} jours`).join(', ')}>
                              <Chip 
                                label={`${joursPermanences} jours`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            `${joursPermanences} jours`
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {sheet.vacancesPeriodes?.length > 0 ? (
                          <Tooltip title={sheet.vacancesPeriodes.map(v => `${v.nom}: ${v.joursOuvres} jours`).join(', ')}>
                            <Chip 
                              label={`${sheet.vacancesPeriodes.length} période(s) - ${sheet.totalJoursVacances || 0} jours`} 
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          "Aucune"
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditServiceSheet(sheet)}
                          color="primary"
                          sx={{ mr: 1 }}
                          title="Modifier"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPrintServiceSheetId(sheet.id);
                            setPrintDialogOpen(true);
                          }}
                          color="primary"
                          sx={{ mr: 1 }}
                          title="Imprimer"
                        >
                          <PrintIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteServiceSheet(sheet.id)}
                          color="error"
                          title="Supprimer"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {serviceSheets.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Aucune fiche de service trouvée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Nouvel onglet pour la gestion des années scolaires */}
        <TabPanel value={tabValue} index={5}>
          <SchoolYearManager />
        </TabPanel>

        {/* Nouvel onglet pour les rapports de migration */}
        <TabPanel value={tabValue} index={6}>
          <MigrationReports />
        </TabPanel>
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Modifier le compte</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Prénom"
                  value={selectedAccount?.firstName || ''}
                  onChange={(e) => setSelectedAccount({ ...selectedAccount, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={selectedAccount?.lastName || ''}
                  onChange={(e) => setSelectedAccount({ ...selectedAccount, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedAccount?.canCreatePermanences || false}
                      onChange={(e) => setSelectedAccount({ ...selectedAccount, canCreatePermanences: e.target.checked })}
                    />
                  }
                  label="Autoriser à créer des permanences"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleEditAccount} 
            variant="contained" 
            color="primary"
            className="edit-account-save-btn"
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
      
      {VacancesDialog()}
      {ServiceSheetDialog()}
      
      {/* Dialogue pour afficher la fiche récapitulative */}
      <Dialog 
        open={summaryDialogOpen} 
        onClose={() => setSummaryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {currentServiceSheet && (
            <ServiceSheetSummary 
              serviceSheet={currentServiceSheet} 
              establishmentInfo={establishmentInfo}
              onClose={() => setSummaryDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialogue d'impression de fiche de service */}
      <PrintableServiceSheet
        serviceSheetId={selectedPrintServiceSheetId}
        establishmentId={userData?.establishmentId}
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
      />
    </Container>
  );
};

export default Settings;
